import React, { useState, useEffect } from 'react';
import { Form, Input, Button, Card, Typography, message, Spin } from 'antd';
import { UserOutlined, LockOutlined, LoginOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@packages/api-client';
import type { LoginRequest } from '@packages/api-client/src/types';

const { Title, Text } = Typography;

interface LoginFormData {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // 从 URL 参数获取登录后要跳转的路径
  const redirectPath = searchParams.get('redirect') || '/';

  // 检查是否已经登录
  useEffect(() => {
    if (apiClient.isAuthenticated()) {
      // 如果已经登录，跳转到原来的页面
      navigate(redirectPath, { replace: true });
    }
  }, [navigate, redirectPath]);

  // 处理登录提交
  const handleLogin = async (values: LoginFormData) => {
    setLoading(true);

    try {
      const loginData: LoginRequest = {
        grant_type: 'urn:aialign:grant-type:password',
        username: values.username,
        password: values.password,
        scope: 'api'
      };

      const result = await apiClient.login(loginData);
      console.log('result====', result);

      // message.success(`欢迎回来，${result.username}！`);
      message.success(`登陆成功！`);

      // 登录成功后跳转到原来的页面
      navigate(redirectPath, { replace: true });
    } catch (error: any) {
      console.error('登录失败:', error);
      message.error(error.message || '登录失败，请重试');
    } finally {
      setLoading(false);
    }
  };

  // 处理登录失败
  const handleLoginFailed = (errorInfo: any) => {
    console.log('表单验证失败:', errorInfo);
    message.error('请检查输入信息');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <Card
        className="w-full max-w-md shadow-xl border-0"
        styles={{ body: { padding: '40px' } }}
      >
        {/* 头部标题 */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <LoginOutlined className="text-white text-2xl" />
          </div>
          <Title level={2} className="!mb-2 !text-gray-800">
            欢迎登录
          </Title>
          <Text type="secondary">IntelliX 本体管理系统</Text>
        </div>

        {/* 登录表单 */}
        <Form
          form={form}
          name="login"
          layout="vertical"
          onFinish={handleLogin}
          onFinishFailed={handleLoginFailed}
          autoComplete="off"
          size="large"
        >
          <Form.Item
            label="用户名"
            name="username"
            rules={[
              { required: true, message: '请输入用户名' },
              { min: 2, message: '用户名至少2个字符' },
            ]}
          >
            <Input
              prefix={<UserOutlined className="text-gray-400" />}
              placeholder="请输入用户名"
              autoComplete="username"
            />
          </Form.Item>

          <Form.Item
            label="密码"
            name="password"
            rules={[
              { required: true, message: '请输入密码' },
              { min: 5, message: '密码至少5个字符' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="text-gray-400" />}
              placeholder="请输入密码"
              autoComplete="current-password"
            />
          </Form.Item>

          <Form.Item className="!mb-6">
            <Button
              type="primary"
              htmlType="submit"
              loading={loading}
              className="w-full h-12 text-lg font-medium"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spin size="small" className="mr-2" />
                  登录中...
                </>
              ) : (
                '登录'
              )}
            </Button>
          </Form.Item>
        </Form>

        {/* 底部信息 */}
        <div className="text-center">
          <Text type="secondary" className="text-sm">
          </Text>
        </div>
      </Card>
    </div>
  );
};

export default Login;
